import { Loader2, Mic, RotateCcw, Square } from "lucide-react";

import { Button } from "@src/shared/components/ui/button";
import { cn } from "@src/shared/lib/utils";

import { useVoiceInput } from "../../application";
import {
  DEFAULT_LABELS,
  STATUS_MESSAGES,
  type UseVoiceInputResult,
  type VoiceInputButtonLabels,
  type VoiceInputConfig,
  type VoiceInputState,
} from "../../domain";

interface VoiceInputButtonBaseProps {
  config: VoiceInputConfig;
  onTranscript: (text: string, transcriptionId: string) => void;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  iconClassName?: string;
  labels?: VoiceInputButtonLabels;
}

export type VoiceInputButtonProps = VoiceInputButtonBaseProps;

export interface VoiceInputButtonViewProps extends UseVoiceInputResult {
  className?: string;
  buttonClassName?: string;
  iconClassName?: string;
  disabled: boolean;
  isIdle: boolean;
  isRecording: boolean;
  isTranscribing: boolean;
  isError: boolean;
  buttonLabel: string;
  statusMessage: string;
  primaryAction: () => void;
}

export function VoiceInputButton(props: VoiceInputButtonProps) {
  const {
    config,
    onTranscript,
    disabled = false,
    className,
    buttonClassName,
    iconClassName,
    labels,
  } = props;
  const voiceInput = useVoiceInput({ config, onTranscript });
  const isIdle = voiceInput.state === "idle";
  const isRecording = voiceInput.state === "recording";
  const isTranscribing = voiceInput.state === "transcribing";
  const isProcessing = voiceInput.state === "processing";
  const isError = voiceInput.state === "error";
  const resolvedLabels = { ...DEFAULT_LABELS, ...labels };
  const buttonLabel = voiceInput.isSupported
    ? resolvedLabels[voiceInput.state]
    : resolvedLabels.unsupported;

  const viewProps: VoiceInputButtonViewProps = {
    ...voiceInput,
    className,
    buttonClassName,
    iconClassName,
    disabled:
      disabled || !voiceInput.isSupported || isTranscribing || isProcessing,
    isIdle,
    isRecording,
    isTranscribing,
    isError,
    buttonLabel,
    statusMessage: STATUS_MESSAGES[voiceInput.state],
    primaryAction: () => {
      if (isRecording) {
        voiceInput.stopRecording();
        return;
      }

      if (isError) {
        voiceInput.dismissError();
      }

      void voiceInput.startRecording();
    },
  };

  return <VoiceInputButtonView {...viewProps} />;
}

export function VoiceInputButtonView({
  className,
  buttonClassName,
  iconClassName,
  ...renderProps
}: VoiceInputButtonViewProps) {
  return (
    <div className={cn("inline-flex items-center", className)}>
      <Button
        aria-label={renderProps.buttonLabel}
        className={cn(
          "h-auto gap-1.5 rounded-[8px] px-1.5 py-1 text-[11px] font-medium text-muted-foreground shadow-none hover:bg-primary/5 hover:text-primary disabled:opacity-45",
          renderProps.isRecording && "text-destructive hover:text-destructive",
          (renderProps.isTranscribing || renderProps.state === "processing") &&
            "text-primary hover:text-primary",
          renderProps.isError && "text-destructive hover:text-destructive",
          buttonClassName,
        )}
        disabled={renderProps.disabled}
        onClick={renderProps.primaryAction}
        type="button"
        variant="ghost"
      >
        <VoiceInputButtonIcon
          className={iconClassName}
          state={renderProps.state}
        />
        {renderProps.buttonLabel}
      </Button>
      <span className="sr-only" role="status" aria-live="polite">
        {renderProps.error ?? renderProps.statusMessage}
      </span>
    </div>
  );
}

function VoiceInputButtonIcon({
  className,
  state,
}: {
  className?: string;
  state: VoiceInputState;
}) {
  switch (state) {
    case "recording":
      return <Square aria-hidden="true" className={cn("size-5", className)} />;
    case "transcribing":
    case "processing":
      return (
        <Loader2
          aria-hidden="true"
          className={cn("size-5 animate-spin", className)}
        />
      );
    case "error":
      return (
        <RotateCcw aria-hidden="true" className={cn("size-5", className)} />
      );
    case "idle":
      return <Mic aria-hidden="true" className={cn("size-5", className)} />;
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}
